export const PROFILE_STEPS = [
  'area',
  'crm',
  'situation',
  'graduation',
  'origin',
] as const

export type ProfileStep = (typeof PROFILE_STEPS)[number]

const SIGNUP_STEPS = ['name', 'cpf', 'email', 'code', 'password'] as const

export type SignupStep = (typeof SIGNUP_STEPS)[number]
export type FlowStep = SignupStep | ProfileStep

const NO_CRM_KEY = 'signup-no-crm'

export type ProfileStepUser = {
  landing_channel?: string | null
  profile?: {
    practice_area?: string | null
    crm?: string | null
    medical_status?: string | null
    graduation_date?: string | null
  } | null
} | null

export function isMedicine(area: string | null | undefined) {
  if (!area) return false

  return (
    area
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim()
      .toLowerCase() === 'medicina'
  )
}

export function setNoCrmChoice(value: boolean) {
  if (value) {
    sessionStorage.setItem(NO_CRM_KEY, '1')
    return
  }

  sessionStorage.removeItem(NO_CRM_KEY)
}

function hasNoCrmChoice() {
  try {
    return sessionStorage.getItem(NO_CRM_KEY) === '1'
  } catch {
    return false
  }
}

export function profilePath(user: ProfileStepUser): ProfileStep[] {
  const profile = user?.profile
  const steps: ProfileStep[] = ['area']
  const medicine = !profile?.practice_area || isMedicine(profile.practice_area)

  if (medicine) {
    steps.push('crm')
    if (!profile?.crm) {
      steps.push('situation')
      const status = profile?.medical_status
      if (!status || status === 'enrolled_brazil') steps.push('graduation')
    }
  }

  steps.push('origin')
  return steps
}

export function nextProfileStep(user: ProfileStepUser): ProfileStep | null {
  const profile = user?.profile
  if (!profile?.practice_area) return 'area'

  if (isMedicine(profile.practice_area)) {
    if (!profile.crm && !profile.medical_status) {
      return hasNoCrmChoice() ? 'situation' : 'crm'
    }

    if (
      !profile.crm &&
      profile.medical_status === 'enrolled_brazil' &&
      !profile.graduation_date
    ) {
      return 'graduation'
    }
  }

  if (!user?.landing_channel) return 'origin'
  return null
}

export function previousProfileStep(
  current: ProfileStep,
  user: ProfileStepUser,
): ProfileStep | null {
  const path = profilePath(user)
  const index = path.indexOf(current)
  if (index <= 0) return null
  return path[index - 1]
}

export function profileStepRedirect(
  current: ProfileStep,
  user: ProfileStepUser,
): ProfileStep | 'home' | null {
  const next = nextProfileStep(user)
  if (!next) return 'home'
  if (PROFILE_STEPS.indexOf(current) > PROFILE_STEPS.indexOf(next)) return next
  return null
}

export function flowProgress(step: FlowStep, user: ProfileStepUser): number {
  const path: FlowStep[] = [...SIGNUP_STEPS, ...profilePath(user)]
  const index = path.indexOf(step)
  if (index < 0) return 0
  return Math.round(((index + 1) / path.length) * 100)
}

export function estimatedSemester(
  year: number,
  month: number,
  now = new Date(),
) {
  const months = (year - now.getFullYear()) * 12 + (month - now.getMonth())
  const remaining = now.getDate() > 1 ? months - 1 : months
  const semester = 12 - Math.floor(remaining / 6)
  if (semester > 12) return 0
  return Math.max(1, semester)
}

export function graduationDate(year: number, month: number) {
  return `${year}-${String(month + 1).padStart(2, '0')}-01`
}

const graduationNow = new Date()
const graduationCurrentYear = graduationNow.getFullYear()
const graduationCurrentMonth = graduationNow.getMonth()

export const graduationYearMin = graduationCurrentYear
export const graduationYearMax = graduationCurrentYear + 7

export function isGraduationMonthEnabled(year: number, month: number) {
  return !(year === graduationCurrentYear && month <= graduationCurrentMonth)
}
